export const namespaced = true;

import NotificationService from '@/services/NotificationService.js';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import map from 'lodash/map';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import moment from 'moment-timezone';

export const state = {
  unpublishedNotifications: [],
  publishingNotifications: [],
  archivedNotifications: [],
  notification: {},
  categoryOptions: [
    { value: '極 - 図形', text: '極 - 図形' },
    { value: '極 - 数', text: '極 - 数' },
    { value: '国語', text: '国語' },
    { value: '遊', text: '遊' },
    { value: '悟', text: '悟' },
    { value: 'その他', text: 'その他' },
  ],
  recipientOptions: [
    { id: '1', name: '管理・先生・生徒' },
    { id: '2_3', name: '管理・先生' },
    { id: '2', name: '管理' },
    { id: '3', name: '先生' },
    { id: '4', name: '生徒' },
  ],
};

export const mutations = {
  SET_UNPUBLISHED_NOTIFICATIONS(state, notifications) {
    state.unpublishedNotifications = notifications;
  },
  SET_PUBLISHING_NOTIFICATIONS(state, notifications) {
    state.publishingNotifications = notifications;
  },
  SET_ARCHIVED_NOTIFICATIONS(state, notifications) {
    state.archivedNotifications = notifications;
  },
  SET_NOTIFICATION(state, notification) {
    state.notification = notification;
  },
  UPDATE_NOTIFICATION(state, notification) {
    state.notification = notification;
  },
  ADD_NOTIFICATION(state, notification) {
    state.notification = notification;
  },
  DELETE_NOTIFICATION() {
    // We only delete Notification, so we don't need to do anything here
  },
};

export const actions = {
  fetchPublishingNotifications({ commit, state }, type) {
    return NotificationService.all({ type }).then((response) => {
      const notifications = map(response.data, (notification) => formatNotification(notification, state));
      commit('SET_PUBLISHING_NOTIFICATIONS', notifications);
    });
  },
  fetchUnpublishedNotifications({ commit, state }, type) {
    return NotificationService.all({ type }).then((response) => {
      const notifications = map(response.data, (notification) => formatNotification(notification, state));
      commit('SET_UNPUBLISHED_NOTIFICATIONS', notifications);
    });
  },
  fetchArchivedNotifications({ commit, state }, type) {
    return NotificationService.all({ type }).then((response) => {
      const notifications = map(response.data, (notification) => formatNotification(notification, state));
      commit('SET_ARCHIVED_NOTIFICATIONS', notifications);
    });
  },
  fetchNotification({ commit, state, getters }, id) {
    var notification = getters.getNotificationById(id);

    if (notification) {
      commit('SET_NOTIFICATION', notification);
      return notification;
    } else {
      return NotificationService.get(id).then((response) => {
        var formattedNotification = formatNotification(response.data, state);
        commit('SET_NOTIFICATION', formattedNotification);
        return formattedNotification;
      });
    }
  },
  finishEditingNotification({ commit, state }, notification) {
    let formattedNotification = cloneDeep(notification);
    if (formattedNotification.startDateObject) {
      formattedNotification.startDatetime = formattedNotification.startDateObject;
      formattedNotification = omit(formattedNotification, ['startDateObject']);
    }
    if (formattedNotification.endDateObject) {
      formattedNotification.endDatetime = formattedNotification.endDateObject;
      formattedNotification = omit(formattedNotification, ['endDateObject']);
    }
    if (formattedNotification.recipientIds) {
      formattedNotification.recipientIds = formattedNotification.recipientIds.split('_');
    }
    if (formattedNotification.recipientOptions) {
      formattedNotification = omit(formattedNotification, ['recipientOptions']);
    }
    if (notification.id) {
      return NotificationService.update(notification.id, formattedNotification).then((response) => {
        commit('UPDATE_NOTIFICATION', formatNotification(response.data, state));
      });
    } else {
      return NotificationService.create(formattedNotification).then((response) => {
        commit('ADD_NOTIFICATION', formatNotification(response.data, state));
      });
    }
  },
  deleteNotification({ commit }, id) {
    return NotificationService.delete(id).then(() => {
      commit('DELETE_NOTIFICATION');
    });
  },
};

export const getters = {
  getNotificationById: (state) => (id) => {
    const notifications = flatten([
      state.unpublishedNotifications,
      state.publishingNotifications,
      state.archivedNotifications,
    ]);

    return notifications.find((item) => item.id == id);
  },
  getUnpublishedNotifications: (state) => {
    return state.unpublishedNotifications;
  },
  getPublishingNotifications: (state) => {
    return state.publishingNotifications;
  },
  getArchivedNotifications: (state) => {
    return state.archivedNotifications;
  },
  totalUnpublishedNotifications: (state, getters) => {
    return getters.getUnpublishedNotifications.length;
  },
  totalPublishingNotifications: (state, getters) => {
    return getters.getPublishingNotifications.length;
  },
  totalArchivedNotifications: (state, getters) => {
    return getters.getArchivedNotifications.length;
  },
};

const formatNotification = (notification, state) => {
  if (notification.startDatetime) {
    let startDateObject = moment(notification.startDatetime);
    notification.startDateObject = {
      year: startDateObject.format('YYYY'),
      month: startDateObject.format('M'),
      day: startDateObject.format('D'),
    };
  }
  if (notification.endDatetime) {
    let endDateObject = moment(notification.endDatetime);
    notification.endDateObject = {
      year: endDateObject.format('YYYY'),
      month: endDateObject.format('M'),
      day: endDateObject.format('D'),
    };
  }
  if (!isEmpty(notification.recipientOptions)) {
    let recipients = [];
    notification.recipientOptions.forEach((recipient) => {
      recipients.push(find(state.recipientOptions, (option) => option.id == recipient.id.toString()));
    });
    if (!isEmpty(recipients)) {
      notification.recipientIds = map(recipients, (recipient) => recipient.id).join('_');
    }
  }
  return notification;
};
